import * as React from 'react';
import { Field } from 'redux-form';

import type { ContactForInvoicesVM } from 'ab-viewModels/contact/contactsForInvoices.viewModel';

import { InvoiceBillingLookupType } from '@acceligentllc/shared/enums/invoiceBillingLookupType';

import Select from 'af-fields/SelectField';

import styles from './styles.module.scss';

const BillingContactSelect = Select as unknown as new () => typeof Select<ContactForInvoicesVM>;

interface BaseContactTableInfo {
	id: number;
	email: string;
	/** ISO_DATETIME */
	lastReminderSent: Nullable<string>;
}

type ContactMethod = BaseContactTableInfo & {
	type: InvoiceBillingLookupType.CONTACT;
	contactId: number;
	title: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;
	contactMethodId: number;
};

type BillingEmail = {
	type: InvoiceBillingLookupType.EMAIL_ONLY;
	email: string;
};

type EmailOnlyInfo = BaseContactTableInfo & BillingEmail;

type ContactTableInfo = EmailOnlyInfo | ContactMethod;

type Props = {
	billingContacts: ContactTableInfo[];
	availableBillingContacts: ContactForInvoicesVM[];
	change: (fieldName: string, value: Nullable<ContactTableInfo>) => void;
	currentContact: ContactTableInfo;
	fieldName: string;
	emailValidationError: Nullable<string>;
	onCreateNew: (contact: ContactTableInfo, name: string, fieldName: string) => void;
};

const getContactOptionValue = (_contact: ContactForInvoicesVM) => {
	return _contact.id;
};

const formatContactOptionLabel = (option: ContactTableInfo, { context }) => {
	if (context === 'value' && option.type === InvoiceBillingLookupType.EMAIL_ONLY) {
		return (
			<div className={styles['contact-menu-selected']}>
				{option.email}
			</div>
		);
	}
	const contactOption = option as ContactMethod; // EmailOnly type is handled above
	const companyName = `${contactOption.companyName ?? ''}`;
	const delimiter = (contactOption.companyName && contactOption.title) ? '|' : '';
	const title = `${contactOption.title ?? ''}`;

	if (context === 'value') {
		return (
			<div className={styles['contact-menu-selected']}>
				<b>{contactOption.fullName}</b> {option.email}
			</div>
		);
	} else
		if (context === 'menu') {
			return (
				<div className={styles['contact-menu-item']}>
					<b>{contactOption.fullName}</b>
					{(companyName || title) && (
						<div>{`${companyName} ${delimiter} ${title}`}</div>
					)}
					<div>{option.email}</div>
				</div>
			);
		}
	return;

};

const _searchContacts = (inputValue: string) =>
	(_contact: ContactForInvoicesVM) => {
		const lowerSearchText = inputValue.toLowerCase();
		return !!_contact.fullName.toLowerCase().includes(lowerSearchText)
			|| !!_contact.companyName?.toLowerCase().includes(lowerSearchText)
			|| !!_contact.title?.toLowerCase().includes(lowerSearchText);
	};

const BillingContactField: React.FC<Props> = ({
	emailValidationError,
	availableBillingContacts,
	change,
	currentContact,
	fieldName,
	onCreateNew,
}) => {
	const [inputValue, setInputValue] = React.useState<Nullable<string>>(null);
	const [filteredOptions, setFilteredOptions] = React.useState<ContactForInvoicesVM[]>(availableBillingContacts);

	const isCreatingNewRef = React.useRef(false);

	const onInputChange = React.useCallback((_inputValue: string) => {
		isCreatingNewRef.current = false;

		setFilteredOptions(availableBillingContacts.filter(_searchContacts(_inputValue)));
		setInputValue(_inputValue);
	}, [availableBillingContacts]);

	const onContactChange = React.useCallback((data: ContactForInvoicesVM) => {
		isCreatingNewRef.current = false;
		setInputValue(null);

		const billingContact: ContactMethod = {
			// Fake ID
			id: data.contactMethodId,
			type: InvoiceBillingLookupType.CONTACT,
			contactId: data.contactId,
			companyName: data.companyName,
			email: data.email,
			fullName: data.fullName,
			title: data.title ?? null,
			lastReminderSent: null,
			contactMethodId: data.contactMethodId,
		};

		change(fieldName, billingContact);
	}, [change, fieldName]);

	const onBlur = React.useCallback(() => {
		if (inputValue && !isCreatingNewRef.current) {
			change(fieldName, {
				id: currentContact.id,
				type: InvoiceBillingLookupType.EMAIL_ONLY,
				email: inputValue,
				lastReminderSent: null,
			});
		}
	}, [change, currentContact.id, fieldName, inputValue]);

	const onClear = React.useCallback(() => {
		isCreatingNewRef.current = false;

		change(fieldName, {
			id: currentContact.id,
			type: InvoiceBillingLookupType.EMAIL_ONLY,
			email: '',
			lastReminderSent: null,
		});
	}, [change, currentContact.id, fieldName]);

	const handleCreateNew = React.useCallback((name: string) => {
		isCreatingNewRef.current = true;
		onCreateNew(currentContact, name, fieldName);
	}, [currentContact, fieldName, onCreateNew]);

	const filterContacts = React.useCallback(async (option: { data: ContactForInvoicesVM & { __isNew__?: boolean; }; }, searchText: string) => {
		if (!option || option.data.__isNew__) {
			return false;
		}

		const lowerSearchText = searchText.toLowerCase();

		return !!option.data.fullName.toLowerCase().includes(lowerSearchText)
			|| !!option.data.companyName?.toLowerCase().includes(lowerSearchText)
			|| !!option.data.title?.toLowerCase().includes(lowerSearchText);
	}, []);

	const showErrorMessage = React.useCallback(() => {
		return (
			<>
				{emailValidationError && (
					<span className={styles['error-message']}>
						<span className="icon-info" />  {emailValidationError}
					</span>
				)}
			</>
		);
	}, [emailValidationError]);

	return (
		<div className={`${styles['contact-dropdown']} ${emailValidationError ? styles['error-exists'] : ''}`}>
			<Field
				addonComponent={showErrorMessage}
				allowNew={true}
				component={BillingContactSelect}
				filterOption={filterContacts}
				fixed={true}
				formatOptionLabel={!!filteredOptions && formatContactOptionLabel}
				getOptionValue={getContactOptionValue}
				inputValue={inputValue}
				name={fieldName}
				onBlur={onBlur}
				onClear={onClear}
				onCreateNew={handleCreateNew}
				onInputChange={onInputChange}
				onValueChange={onContactChange}
				options={filteredOptions}
				placeholder="Choose Contact or Enter Email"
				styles={{
					menuPortal: (base) => ({ ...base, zIndex: 99999 }),
				}}
			/>
		</div>
	);

};

export default React.memo(BillingContactField);
